/**
 * The Agents panel: the agents a chat's replies started, in the drafts
 * panel's frame. Each row is an agent's title and how it stands; opened, the
 * agent's own chat, watch-only, with the reply it is writing drawn as it
 * comes, and Stop while it works.
 *
 * Agent rows are app state's (`useAgents`), kept by the hub. The watch view's
 * turn therefore writes no rows of its own: a turn's `done` can reach this
 * page after the hub's news of how the agent ended, and would put it back to
 * working.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getChat, stopReply, type AgentState, type Chat, type ChatSummary } from '../api';
import { ds } from '../ds';
import { toSummary } from '../hooks/chatStash';
import { useStickToBottom } from '../hooks/useStickToBottom';
import { attachTurn, forgetTurn, stopTurn, useLiveTurn, withTurn, type TurnApp } from '../hooks/useTurns';
import { useApp } from '../state';
import { STATE_TEXT } from './agentsText';
import { PanelFrame, PanelTitle } from './ArtifactPanel';
import { MessageList } from './MessageList';
import { Ph } from './ui';

const ICON: Record<AgentState, { name: string; colour: string }> = {
  working: { name: 'sparkle', colour: 'var(--primary)' },
  done: { name: 'check-circle', colour: 'var(--signal)' },
  failed: { name: 'warning-circle', colour: 'var(--danger-500)' },
  stopped: { name: 'stop-circle', colour: 'var(--text-faint)' }
};

const stateOf = (a: ChatSummary): AgentState => a.agent?.state ?? 'working';

function AgentRow({ agent, onOpen }: { agent: ChatSummary; onOpen: () => void }) {
  const state = stateOf(agent);
  return (
    <button
      type="button"
      className="bare hov-tint"
      onClick={onOpen}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: 10,
        borderRadius: 'var(--radius-app-card)',
        textAlign: 'left'
      }}
    >
      <Ph
        name={ICON[state].name}
        size={16}
        color={ICON[state].colour}
        className={state === 'working' ? 'pulse' : undefined}
        style={{ flex: 'none', marginTop: 2 }}
      />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 13, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {agent.title}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)' }}>{STATE_TEXT[state]}</span>
      </span>
    </button>
  );
}

/** One agent's chat, as it stands and as it is written. */
function AgentView({ agent, onBack }: { agent: ChatSummary; onBack: () => void }) {
  const { patchMe, refreshMe } = useApp();
  const { Button } = ds();
  const app = useMemo<TurnApp>(() => ({ upsertChat: () => {}, touchChat: () => {}, patchMe, refreshMe }), [patchMe, refreshMe]);
  const [chat, setChat] = useState<Chat | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const live = useLiveTurn(agent.id);
  const { scrollRef, contentRef } = useStickToBottom();
  const state = stateOf(agent);

  // A reply begun or ended in it, as the hub tells: the chat as it is now.
  const running = agent.live ? `${agent.id}:${agent.live.messageId ?? ''}` : null;
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    getChat(agent.id).then(
      (c) => {
        if (cancelled) return;
        forgetTurn(agent.id);
        setChat(c);
      },
      () => {
        if (!cancelled) setFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [agent.id, running, state, attempt]);

  // Joined once for each reply, to draw it as it comes.
  const joined = useRef<string | null>(null);
  useEffect(() => {
    if (!chat || !running || joined.current === running || live?.phase === 'streaming') return;
    joined.current = running;
    attachTurn({ chat: toSummary(chat), app });
  }, [running, chat]);

  const messages = useMemo(() => (chat ? withTurn(chat.messages, live) : []), [chat, live]);

  const stop = () => {
    if (live?.phase === 'streaming') stopTurn(agent.id);
    else void stopReply(agent.id).catch(() => undefined);
  };

  return (
    <>
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minHeight: 44,
          padding: '6px 10px 6px 6px',
          borderBottom: '1px solid var(--border-hairline)'
        }}
      >
        <button
          type="button"
          className="bare hov-soft"
          aria-label="All agents"
          onClick={onBack}
          style={{ width: 30, height: 30, flex: 'none', display: 'grid', placeItems: 'center', borderRadius: 6, color: 'var(--text-muted)' }}
        >
          <Ph name="caret-left" size={15} />
        </button>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {agent.title}
        </span>
        {state === 'working' ? (
          <Button variant="ghost" density="app" size="sm" onClick={stop}>
            Stop
          </Button>
        ) : (
          <span style={{ flex: 'none', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)' }}>{STATE_TEXT[state]}</span>
        )}
      </div>
      <div ref={scrollRef} className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div ref={contentRef} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {chat ? (
            <MessageList
              readOnly
              chatId={agent.id}
              messages={messages}
              live={live?.phase === 'streaming' ? live : null}
              busy={live?.phase === 'streaming'}
            />
          ) : failed ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-muted)' }}>
              This agent did not load.
              <button type="button" className="bare" onClick={() => setAttempt((n) => n + 1)} style={{ fontSize: 13.5, color: 'var(--text-link)' }}>
                Retry
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

export function AgentsPanel({
  agents,
  watching,
  onWatch,
  tabs,
  onClose,
  overlay,
  narrow
}: {
  /** The chat's agents, oldest first. */
  agents: ChatSummary[];
  /** The agent open, or null for the list. */
  watching: string | null;
  onWatch: (id: string | null) => void;
  /** Drafts and Agents, where the chat has both. */
  tabs?: ReactNode;
  onClose: () => void;
  overlay: boolean;
  narrow: boolean;
}) {
  const watched = watching ? agents.find((a) => a.id === watching) ?? null : null;
  return (
    <PanelFrame head={tabs ?? <PanelTitle>Agents</PanelTitle>} onClose={onClose} overlay={overlay} narrow={narrow}>
      {watched ? (
        <AgentView key={watched.id} agent={watched} onBack={() => onWatch(null)} />
      ) : (
        <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {agents.map((a) => (
            <AgentRow key={a.id} agent={a} onOpen={() => onWatch(a.id)} />
          ))}
        </div>
      )}
    </PanelFrame>
  );
}
